import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import SearchBox from '../../components/SearchBox/SearchBox';
import Pagination from '../../components/Pagination/Pagination';
import { useDRResource } from '../../hooks/queries/drResourceQueries';
import {
  resourceRelationQueryKeys,
  useOpmContractNumberInfoSingle,
  useSetSettlementTargetValue,
} from '../../hooks/queries/resourceRelationQueries';
import { isSystemAdmin } from '../../utils/permissions';
import StationBulkAssignModal from '../DRResourceDetail/components/StationBulkAssignModal.jsx';
import OpmContractAppendModal from './components/OpmContractAppendModal.jsx';
import OpmContractDeleteModal from './components/OpmContractDeleteModal.jsx';
import {
  getOpmContractNumber,
  getDataEndDateString,
  getEndDateHighlightClass,
  matchesConnectFilter,
  matchesSettlementTargetFilter,
  ConnectStatusBadge,
} from '../../components/OpmContractList/OpmContractListHelpers.jsx';
import './PowerMeterContractDetail.css';

const CONTRACT_PAGE_SIZE = 10;

/** DR 상세 StationAccordion 과 동일한 충전기 상태 → 카테고리 */
function getChargerStatusCategory(statusCode) {
  switch (statusCode) {
    case 2:
    case 6:
      return 'waiting';
    case 3:
      return 'using';
    case 0:
    case 1:
    case 4:
    case 5:
    case 9:
    default:
      return 'unavailable';
  }
}

/** 카테고리별 배지 색·라벨 (StationAccordion getStatusInfo 와 동일) */
function getChargerCategoryDisplay(category) {
  switch (category) {
    case 'using':
      return { text: '사용', color: '#29D668', dotColor: '#29D668', bgColor: '#E5FAEA' };
    case 'waiting':
      return { text: '대기', color: '#4FC3F7', dotColor: '#4FC3F7', bgColor: '#EDF9FE' };
    case 'unavailable':
      return { text: '불가', color: '#FEC02E', dotColor: '#FEC02E', bgColor: '#FFFDE7' };
    default:
      return { text: '대기', color: '#4FC3F7', dotColor: '#4FC3F7', bgColor: '#EDF9FE' };
  }
}

function StatusPill({ text, color, dotColor, bgColor }) {
  return (
    <div className="opm-status-pill" style={{ backgroundColor: bgColor }}>
      <span className="opm-status-pill__dot" style={{ backgroundColor: dotColor }} />
      <span className="opm-status-pill__text" style={{ color }}>
        {text}
      </span>
    </div>
  );
}

function ChargerStatusBadge({ status }) {
  const category = getChargerStatusCategory(status);
  const info = getChargerCategoryDisplay(category);
  return <StatusPill {...info} />;
}

/** 충전소 ID 오름차순 → 동일 시 충전기 ID 오름차순 (문자·숫자 혼합 ID 대응) */
function compareOpmSecondItems(a, b) {
  const opt = { numeric: true, sensitivity: 'base' };
  const byStation = String(a?.CpoStationId ?? '').localeCompare(String(b?.CpoStationId ?? ''), undefined, opt);
  if (byStation !== 0) return byStation;
  return String(a?.CpoChargerId ?? '').localeCompare(String(b?.CpoChargerId ?? ''), undefined, opt);
}

const PowerMeterContractDetail = () => {
  const { drResourceSysId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isSystemAdminUser = isSystemAdmin();
  const [stationBulkAssignModalOn, setStationBulkAssignModalOn] = useState(false);
  const [appendModalOn, setAppendModalOn] = useState(false);
  const [deleteTargetContractNumber, setDeleteTargetContractNumber] = useState(null);
  const [selectedContractNumber, setSelectedContractNumber] = useState(null);
  const [contractListPage, setContractListPage] = useState(1);
  /** SearchBox에서 검색(엔터/아이콘) 시 적용되는 계약번호 필터 */
  const [contractSearchApplied, setContractSearchApplied] = useState('');
  /** OPM데이터제공상태: 전체 | 연결 | 미연결 */
  const [connectFilter, setConnectFilter] = useState('all');
  /** 정산대상여부: 전체 | 대상 | 비대상 */
  const [settlementTargetFilter, setSettlementTargetFilter] = useState('all');

  const {
    data: drResource,
    isLoading: drLoading,
    isError: drIsError,
    error: drError,
    refetch: refetchDrResource,
  } = useDRResource(drResourceSysId);

  const handleBulkAssignSuccess = () => {
    refetchDrResource();
    queryClient.invalidateQueries({
      queryKey: [...resourceRelationQueryKeys.all, 'opmContractNumberInfo'],
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: resourceRelationQueryKeys.opmContractNumberInfoSummary(drResourceSysId),
    });
  };

  const setSettlementTargetMutation = useSetSettlementTargetValue();

  const handleSettlementTargetChange = (contractNumber, value) => {
    if (!drResourceSysId || !contractNumber) return;
    setSettlementTargetMutation.mutate(
      { drResourceSysId, contractNumber, value },
      {
        onSuccess: () => handleBulkAssignSuccess(),
        onError: (error) => {
          console.error('정산대상 여부 변경 실패:', error);
        },
      }
    );
  };

  const handleAppendSuccess = handleBulkAssignSuccess;

  const handleDeleteSuccess = () => {
    if (deleteTargetContractNumber === selectedContractNumber) {
      setSelectedContractNumber(null);
    }
    handleBulkAssignSuccess();
  };

  const opmList = useMemo(() => {
    const raw = drResource?.OpmContractInfos;
    return Array.isArray(raw) ? raw : [];
  }, [drResource]);

  const filteredOpmList = useMemo(() => {
    let list = opmList;
    if (connectFilter !== 'all') {
      list = list.filter((row) => matchesConnectFilter(row, connectFilter));
    }
    if (settlementTargetFilter !== 'all') {
      list = list.filter((row) => matchesSettlementTargetFilter(row, settlementTargetFilter));
    }
    const q = contractSearchApplied.trim().toLowerCase();
    if (q) {
      list = list.filter((row) => {
        const num = String(getOpmContractNumber(row)).toLowerCase();
        return num.includes(q);
      });
    }
    return list;
  }, [opmList, contractSearchApplied, connectFilter, settlementTargetFilter]);

  const contractListSummary = useMemo(() => {
    const total = opmList.length;
    const settlementTargetTotal = opmList.filter((r) => r.IsSettlementTarget === true).length;
    const shown = filteredOpmList.length;
    const searchQ = contractSearchApplied.trim();

    const connectLabels = { all: '전체', connected: '제공', disconnected: '미제공' };
    const settlementLabels = { all: '전체', target: '대상', nonTarget: '비대상' };

    const hasActiveFilters =
      connectFilter !== 'all' || settlementTargetFilter !== 'all' || Boolean(searchQ);

    return {
      total,
      settlementTargetTotal,
      shown,
      searchQ,
      connectLabel: connectLabels[connectFilter],
      settlementLabel: settlementLabels[settlementTargetFilter],
      hasActiveFilters,
    };
  }, [
    opmList,
    filteredOpmList.length,
    contractSearchApplied,
    connectFilter,
    settlementTargetFilter,
  ]);

  const contractTotalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredOpmList.length / CONTRACT_PAGE_SIZE)),
    [filteredOpmList.length]
  );

  const pagedOpmList = useMemo(() => {
    const start = (contractListPage - 1) * CONTRACT_PAGE_SIZE;
    return filteredOpmList.slice(start, start + CONTRACT_PAGE_SIZE);
  }, [filteredOpmList, contractListPage]);

  useEffect(() => {
    setContractListPage((p) => Math.min(p, contractTotalPages));
  }, [contractTotalPages]);

  useEffect(() => {
    if (filteredOpmList.length === 0) {
      setSelectedContractNumber(null);
      return;
    }
    const numbers = filteredOpmList.map(getOpmContractNumber).filter(Boolean);
    setSelectedContractNumber((prev) => {
      if (prev && numbers.includes(prev)) return prev;
      return numbers[0] ?? null;
    });
  }, [filteredOpmList]);

  const handleContractSearch = (value) => {
    setContractSearchApplied(String(value ?? '').trim());
    setContractListPage(1);
  };

  useEffect(() => {
    setContractSearchApplied('');
    setConnectFilter('all');
    setSettlementTargetFilter('all');
    setContractListPage(1);
  }, [drResourceSysId]);

  const handleConnectFilterChange = (next) => {
    setConnectFilter(next);
    setContractListPage(1);
  };

  const handleSettlementTargetFilterChange = (next) => {
    setSettlementTargetFilter(next);
    setContractListPage(1);
  };

  const {
    data: opmDetail,
    isLoading: opmLoading,
    isError: opmIsError,
  } = useOpmContractNumberInfoSingle({
    DrResourceSysId: drResourceSysId,
    PowerContractNumber: selectedContractNumber,
    enabled: !!drResourceSysId && !!selectedContractNumber,
  });

  const firstItems = opmDetail?.FirstItems ?? [];
  const secondItems = opmDetail?.SecondItems ?? [];
  const sortedSecondItems = useMemo(
    () => [...secondItems].sort(compareOpmSecondItems),
    [secondItems]
  );

  const handleBack = () => {
    if (location.state?.from) {
      navigate(-1);
    } else {
      navigate('/dr-management');
    }
  };

  if (drLoading) {
    return (
      <div className="detail-loading">
        <div className="loading-spinner"></div>
        <p className="text-b1-400 text-base">DR 자원 정보를 불러오는 중...</p>
      </div>
    );
  }

  if (drIsError) {
    return (
      <div className="detail-error">
        <h2 className="text-b1-700 text-base">정보를 불러오지 못했습니다</h2>
        <p className="text-b1-400 text-base">{drError?.message || '다시 시도해 주세요.'}</p>
        <button type="button" onClick={() => navigate('/dr-management')} className="btn big-btn btn-primary">
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  if (!drResource) {
    return (
      <div className="detail-not-found">
        <h2 className="text-b1-700 text-base">DR 자원을 찾을 수 없습니다</h2>
        <p className="text-b1-400 text-base">요청하신 DR 자원이 존재하지 않거나 삭제되었습니다.</p>
        <button type="button" onClick={() => navigate('/dr-management')} className="btn big-btn btn-primary">
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="detail-page-container">
      <div className="detail-page-content">
        <div className="detail-page-content-divide-container">
          <div className="detail-page-content-divide-content left">
            <div className="flex align-center mb-5">
              <button type="button" className="btn icon-btn" onClick={handleBack} title="뒤로가기">
                <ChevronLeftIcon color="var(--gray-600)" />
                <span className="text-b2-700 text-gray-600">뒤로가기</span>
              </button>
            </div>
            <div className="detail-page-content-divide-content-header flex align-center gap-5">
              <h3 className="text-b1-700 text-primary">{drResource.ResourceName}</h3>
            </div>
            <p className="text-b2-400 text-gray-600 mt-2">OPM 고객계약번호별 충전 인프라 조회</p>

            <div className="flex flex-col gap-10 mt-10 mb-20">
              <div className="flex align-start justify-between flex-wrap gap-10">
                <div className="flex flex-col gap-8 min-w-0 flex-1">
                  <h3 className="text-b1-700 text-base">계약번호 목록</h3>
                  <div
                    className="opm-contract-list-summary"
                    aria-label="계약 목록 요약"
                  >
                    {contractListSummary.hasActiveFilters && (
                      <div className="opm-contract-list-summary__row opm-contract-list-summary__row--filters">
                        <span className="opm-contract-list-summary__filters-heading">적용 필터</span>
                        <div className="opm-contract-list-summary__tags">
                          <span className="opm-contract-list-summary__tag">
                            OPM 제공{' '}
                            <strong className="opm-contract-list-summary__tag-strong">
                              {contractListSummary.connectLabel}
                            </strong>
                          </span>
                          <span className="opm-contract-list-summary__tag">
                            정산 대상{' '}
                            <strong className="opm-contract-list-summary__tag-strong">
                              {contractListSummary.settlementLabel}
                            </strong>
                          </span>
                          {contractListSummary.searchQ ? (
                            <span className="opm-contract-list-summary__tag">
                              검색{' '}
                              <strong className="opm-contract-list-summary__tag-strong">
                                &quot;{contractListSummary.searchQ}&quot;
                              </strong>
                            </span>
                          ) : null}
                        </div>
                      </div>
                    )}
                    <div className="opm-contract-list-summary__row opm-contract-list-summary__row--stats">
                      {contractListSummary.hasActiveFilters && (
                        <div className="opm-contract-list-summary__stat opm-contract-list-summary__stat--emphasis">
                          <span className="opm-contract-list-summary__stat-label">표시</span>
                          <span className="opm-contract-list-summary__stat-value">
                            {contractListSummary.shown}
                            <span className="opm-contract-list-summary__stat-unit">건</span>
                          </span>
                        </div>
                      )}
                      <div className="opm-contract-list-summary__stat">
                        <span className="opm-contract-list-summary__stat-label">전체</span>
                        <span className="opm-contract-list-summary__stat-value">
                          {contractListSummary.total}
                          <span className="opm-contract-list-summary__stat-unit">건</span>
                        </span>
                      </div>
                      <div className="opm-contract-list-summary__stat">
                        <span className="opm-contract-list-summary__stat-label">정산대상</span>
                        <span className="opm-contract-list-summary__stat-value">
                          {contractListSummary.settlementTargetTotal}
                          <span className="opm-contract-list-summary__stat-unit">건</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {isSystemAdminUser && (
                  <div className="flex align-center gap-5">
                    <button
                      type="button"
                      className="btn small-btn btn-style-5"
                      onClick={() => setAppendModalOn(true)}
                    >
                      단건 추가
                    </button>
                    <button
                      type="button"
                      className="btn small-btn btn-primary"
                      onClick={() => setStationBulkAssignModalOn(true)}
                    >
                      일괄 할당
                    </button>
                  </div>
                )}
              </div>
              <div className="flex gap-20">
                <div
                  className="opm-connect-filter"
                  role="group"
                  aria-label="OPM데이터제공상태 필터"
                >
                  <span className="opm-connect-filter__label text-b2-400 text-gray-600">OPM 데이터 제공 상태 필터</span>
                  <div className="opm-connect-filter__buttons">
                    <button
                      type="button"
                      className={`opm-connect-filter__btn${connectFilter === 'all' ? ' opm-connect-filter__btn--active' : ''}`}
                      onClick={() => handleConnectFilterChange('all')}
                    >
                      전체
                    </button>
                    <button
                      type="button"
                      className={`opm-connect-filter__btn${connectFilter === 'connected' ? ' opm-connect-filter__btn--active' : ''}`}
                      onClick={() => handleConnectFilterChange('connected')}
                    >
                      제공
                    </button>
                    <button
                      type="button"
                      className={`opm-connect-filter__btn${connectFilter === 'disconnected' ? ' opm-connect-filter__btn--active' : ''}`}
                      onClick={() => handleConnectFilterChange('disconnected')}
                    >
                      미제공
                    </button>
                  </div>
                </div>
                <div
                  className="opm-connect-filter"
                  role="group"
                  aria-label="정산대상여부 필터"
                >
                  <span className="opm-connect-filter__label text-b2-400 text-gray-600">
                    정산대상 여부 필터
                  </span>
                  <div className="opm-connect-filter__buttons">
                    <button
                      type="button"
                      className={`opm-connect-filter__btn${settlementTargetFilter === 'all' ? ' opm-connect-filter__btn--active' : ''}`}
                      onClick={() => handleSettlementTargetFilterChange('all')}
                    >
                      전체
                    </button>
                    <button
                      type="button"
                      className={`opm-connect-filter__btn${settlementTargetFilter === 'target' ? ' opm-connect-filter__btn--active' : ''}`}
                      onClick={() => handleSettlementTargetFilterChange('target')}
                    >
                      대상
                    </button>
                    <button
                      type="button"
                      className={`opm-connect-filter__btn${settlementTargetFilter === 'nonTarget' ? ' opm-connect-filter__btn--active' : ''}`}
                      onClick={() => handleSettlementTargetFilterChange('nonTarget')}
                    >
                      비대상
                    </button>
                  </div>
                </div>
              </div>
              <SearchBox
                placeholder="계약번호 검색 (부분 일치)"
                showFilter={false}
                onSearch={handleContractSearch}
                defaultValue={contractSearchApplied}
              />
              {opmList.length === 0 ? (
                <p className="text-b2-400 text-gray-600">등록된 계약번호가 없습니다.</p>
              ) : filteredOpmList.length === 0 ? (
                <p className="text-b2-400 text-gray-600">필터 조건에 맞는 계약이 없습니다.</p>
              ) : (
                <div className="opm-contract-list">
                  <div className="primary-table opm-contract-table">
                    <table>
                      <thead>
                        <tr>
                          <th>계약번호</th>
                          <th>OPM데이터<br/>제공상태</th>
                          <th>정보제공시작일</th>
                          <th>정보제공종료일</th>
                          <th>정산대상여부</th>
                          {isSystemAdminUser && <th width="40px"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {pagedOpmList.map((row) => {
                          const num = getOpmContractNumber(row);
                          const selected = num && num === selectedContractNumber;
                          const isSettlementTarget = Boolean(row.IsSettlementTarget);
                          const settlementRowPending =
                            setSettlementTargetMutation.isPending &&
                            setSettlementTargetMutation.variables?.contractNumber === num;
                          return (
                            <tr
                              key={num || JSON.stringify(row)}
                              className={`primary-table-item-clickable${selected ? ' opm-contract-row-selected' : ''}`}
                              onClick={() => num && setSelectedContractNumber(num)}
                            >
                              <td>{num || '—'}</td>
                              <td>
                                <ConnectStatusBadge connect={row.ConnectStatus} />
                              </td>
                              <td>{row.DataStartDate ?? row.dataStartDate ?? '—'}</td>
                              <td
                                className={getEndDateHighlightClass(getDataEndDateString(row))}
                              >
                                {getDataEndDateString(row) ?? '—'}
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                {isSystemAdminUser ? (
                                  <div
                                    className="opm-settlement-switch"
                                    role="group"
                                    aria-label="정산대상"
                                  >
                                    <div className="opm-settlement-switch__buttons">
                                      <button
                                        type="button"
                                        className={`opm-settlement-switch__btn${
                                          !isSettlementTarget
                                            ? ' opm-settlement-switch__btn--active'
                                            : ''
                                        }`}
                                        disabled={!num || settlementRowPending}
                                        onClick={() => {
                                          if (isSettlementTarget) handleSettlementTargetChange(num, false);
                                        }}
                                      >
                                        N
                                      </button>
                                      <button
                                        type="button"
                                        className={`opm-settlement-switch__btn${
                                          isSettlementTarget
                                            ? ' opm-settlement-switch__btn--active'
                                            : ''
                                        }`}
                                        disabled={!num || settlementRowPending}
                                        onClick={() => {
                                          if (!isSettlementTarget) handleSettlementTargetChange(num, true);
                                        }}
                                      >
                                        Y
                                      </button>
                                    </div>
                                  </div>
                                ) : row.IsSettlementTarget ? (
                                  '대상'
                                ) : (
                                  '비대상'
                                )}
                              </td>
                              {isSystemAdminUser && (
                                <td onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    className="btn icon-btn"
                                    title="삭제"
                                    disabled={!num}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (num) setDeleteTargetContractNumber(num);
                                    }}
                                  >
                                    <DeleteIcon color="var(--gray-600)" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {contractTotalPages > 1 && (
                    <div className="opm-contract-list-pagination">
                      <Pagination
                        currentPage={contractListPage}
                        totalPages={contractTotalPages}
                        onPageChange={setContractListPage}
                        maxVisiblePages={10}
                        size="small"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="detail-page-content-divide-content right">
            <div className="flex flex-col gap-10 mb-20">
              <h3 className="text-b1-700 text-base">
                {selectedContractNumber
                  ? `OPM 전력계약번호 ${selectedContractNumber} 의 자원 목록`
                  : '계약을 선택하면 자원 목록이 표시됩니다'}
              </h3>

              {selectedContractNumber && opmLoading && (
                <p className="text-b2-400 text-gray-600">충전소·충전기 정보를 불러오는 중...</p>
              )}
              {selectedContractNumber && opmIsError && (
                <p className="text-b2-400 text-error">자원 목록을 불러오지 못했습니다.</p>
              )}

              {selectedContractNumber && !opmLoading && !opmIsError && (
                <>
                  <section className="opm-detail-section">
                    <h4 className="detail-page-charger-comp-charger-title left text-b1-700 text-base mb-5">
                      충전소
                      {opmDetail?.FirstCount != null ? (
                        <span className="text-b2-400 text-gray-600"> ({opmDetail.FirstCount})</span>
                      ) : null}
                    </h4>
                    {firstItems.length === 0 ? (
                      <p className="text-b2-400 text-gray-600">연결된 충전소가 없습니다.</p>
                    ) : (
                      <div className="primary-table opm-resource-table">
                        <table>
                          <thead>
                            <tr>
                              <th>충전소명</th>
                              <th>충전소 ID</th>
                              <th>주소</th>
                              <th>CPO명</th>
                              <th>CPO ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {firstItems.map((s) => (
                              <tr key={s.StationSysId ?? `${s.CpoStationId}-${s.CpoStationName}`}>
                                <td>{s.CpoStationName ?? '—'}</td>
                                <td>{s.CpoStationId ?? '—'}</td>
                                <td>{s.Address ?? '—'}</td>
                                <td>{s.CpoName ?? '—'}</td>
                                <td>{s.CpoId ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>

                  <section className="opm-detail-section">
                    <h4 className="detail-page-charger-comp-charger-title left text-b1-700 text-base mb-5">
                      충전기
                      {opmDetail?.SecondCount != null ? (
                        <span className="text-b2-400 text-gray-600"> ({opmDetail.SecondCount})</span>
                      ) : null}
                    </h4>
                    {sortedSecondItems.length === 0 ? (
                      <p className="text-b2-400 text-gray-600">연결된 충전기가 없습니다.</p>
                    ) : (
                      <div className="primary-table opm-resource-table">
                        <table>
                          <thead>
                            <tr>
                              <th>충전소 ID</th>
                              <th>충전기 ID</th>
                              <th>용량(kW)</th>
                              <th>상태</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedSecondItems.map((c) => (
                              <tr key={c.ChargerSysId ?? c.CpoChargerId}>
                                <td>{c.CpoStationId ?? '충전소 미할당'}</td>
                                <td>{c.CpoChargerId ?? '—'}</td>
                                <td>{c.ChargerCapacity != null ? c.ChargerCapacity : '—'}</td>
                                <td>
                                  <ChargerStatusBadge status={c.Status} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {stationBulkAssignModalOn && (
        <StationBulkAssignModal
          onClose={() => setStationBulkAssignModalOn(false)}
          drResource={drResource}
          onSuccess={handleBulkAssignSuccess}
        />
      )}

      {appendModalOn && (
        <OpmContractAppendModal
          onClose={() => setAppendModalOn(false)}
          drResource={drResource}
          onSuccess={handleAppendSuccess}
        />
      )}

      {deleteTargetContractNumber && (
        <OpmContractDeleteModal
          onClose={() => setDeleteTargetContractNumber(null)}
          drResource={drResource}
          contractNumber={deleteTargetContractNumber}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};

export default PowerMeterContractDetail;
